<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue';
import { useRoute, useRouter, RouterView } from 'vue-router';
import { until } from '@vueuse/core';
import { useHead } from '@unhead/vue';
import { captureException } from '@sentry/vue';
import { noop } from 'lodash-es';
import { type DeepPartial } from '@/shared/types';
import { GlobalAnnouncements } from '@/app/modules/announcements';
import { DownloadsWidget } from '@/app/modules/data-center/components';
import { useI18nStore, setI18nLanguage, LOCALES, DEFAULT_LOCALE, useI18n } from '@/app/modules/i18n';
import { useUserStore } from '@/app/modules/user';
import { useAccountStore } from '@/app/modules/account';
import { useSupersetKeyMetaStore } from '@/app/modules/superset';
import { useClarity } from '@/app/modules/clarity';
import {
  ObConfigProvider,
  ObToasts,
  ObConfirmDialog,
  type Config,
  UiConfigProvider,
  type UiConfig,
  UiButton,
  UiEmptyState,
} from '@/ui-kit';
import { useApiClient, type AuthState } from '@/app/core/api-client';
import { useEventBus } from '@/app/core/event-bus';
import { runGuards } from '@/app/core/router';
import { useFlagsStatus, useFeatureFlags } from '@/app/modules/feature-flags';
import { useHeap } from '@/app/modules/heap';
import { formatDatePickerLabel, log } from './shared/utils';
import { useSW } from './use-sw';
import { StoreNames } from './shared/store-names';
import { FullHeightContainer } from './components';

useSW();

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const accountStore = useAccountStore();
const i18nStore = useI18nStore();
const { t, n, d, locale } = useI18n();
const api = useApiClient();
const featureFlags = useFeatureFlags();
const { flagsReady } = useFlagsStatus();
const keyMetaStore = useSupersetKeyMetaStore();
const heap = useHeap();
const eventBus = useEventBus();
const clarity = useClarity();

const initializing = ref(true);
const error = ref(false);

async function reRunCurrentRouteGuards() {
  const result = await runGuards(route, route);

  if (result === false) {
    await router.push('/');
  } else if (typeof result !== 'undefined' && result !== true) {
    await router.push(result);
  }
}

async function handleAuthStateChange(state: AuthState, initial?: boolean): Promise<void> {
  if (state === 'error') {
    error.value = true;
    return;
  }

  if (state === 'authenticated') {
    await Promise.all([userStore.fetch(), accountStore.fetch()]);

    try {
      await keyMetaStore.fetch();
    } catch (err) {
      console.error(`Failed to fetch ${StoreNames.SupersetKeyMeta}:`, err);
    }

    const domain = userStore.email.split('@')[1];

    heap.identify(userStore.id);
    heap.addUserProperties({
      account_id: accountStore.id,
      account_name: accountStore.name,
      domain,
    });

    clarity?.identify(userStore.id);
    clarity?.setTag('account_id', accountStore.id);
    clarity?.setTag('account_name', accountStore.name);
    clarity?.setTag('domain', domain);

    featureFlags.setContextField('userId', userStore.id);
    featureFlags.setContextField('accountId', accountStore.id);
  } else {
    userStore.clear();
    accountStore.clear();

    heap.resetIdentity();

    featureFlags.setContextField('userId', '');
    featureFlags.setContextField('accountId', '');
  }

  // Simply check if user can stay on current route
  if (!initial) {
    reRunCurrentRouteGuards();
    // TODO: handle possible error
  }
}

const uiConfig = computed<Config>(() => {
  const config: Config = {
    numberFormat: {
      decimalSeparator: i18nStore.decimalSeparator,
      thousandSeparator: i18nStore.thousandSeparator,
    },
    i18n: {
      BatchActionsMenu: {
        labelSelectedItems(count) {
          return t('common.messages.itemsSelected', count);
        },
      },
      FileUploader: {
        labelTrigger: t('ui.fileUploader.labelTrigger'),
      },
      TablePagination: {
        labelRowsPerPage: t('ui.TablePagination.labelRowsPerPage'),
        labelDisplayedRows(currentPageStart, currentPageEnd, total) {
          return t('ui.TablePagination.labelDisplayedRows', {
            min: n(currentPageStart, 'pretty'),
            max: n(currentPageEnd, 'pretty'),
            total: n(total, 'pretty'),
          });
        },
      },
      DatePicker: {
        label: (value, selectionMode) => formatDatePickerLabel(value, selectionMode, (date) => d(date, 'date')),
      },
    },
  };

  return config;
});

const uiConfigNew = computed<DeepPartial<UiConfig>>(() => ({
  i18n: {
    dataTable: {
      columnsSettingsTitle: t('common.messages.columnsVisibilitySelectionHint'),
      columnsSettingsSearchHint: t('common.messages.columnsSearchHint'),
      openColumnsSettings: t('common.actions.openColumnsVisibilitySettings'),
      noData: 'No data',
      itemsSelected: (count) => `${n(count, 'pretty')} ${t('common.messages.itemsSelected', count)}`, // TODO: make nItemsSelected key with place for the number
      selectAllItems: (count) =>
        ` ${t('common.actions.selectAll')} ${n(count, 'pretty')} ${t('common.plurals.items', count)}`, // TODO: make selectAllNItems key with place for the number
      clearSelection: t('common.actions.clearSelection'),
      sortAscending: t('ui.sortAscending'),
      sortDescending: t('ui.sortDescending'),
      pinColumn: t('ui.pinColumn'),
      unpinColumn: t('ui.unpinColumn'),
      hideColumn: t('ui.hideColumn'),
    },
  },
}));

onMounted(async () => {
  log('initializing app');

  try {
    // Handle initial auth state manually
    await handleAuthStateChange(api.auth.getState(), true);

    api.auth.onStateChanged((event) => handleAuthStateChange(event, false));

    // Use previously selected locale (in future can be saved in user profile) or account default locale or browser locale...
    // ...but only if locale is supported by the app.

    log('setting language');

    let locale = i18nStore.locale || accountStore.locale || window.navigator.language;

    if (!LOCALES.find(({ code }) => code === locale)) {
      locale = DEFAULT_LOCALE;
    }

    setI18nLanguage(locale);
    i18nStore.setLocale(locale);

    try {
      log('starting unleash client');
      await featureFlags.start();
      await until(flagsReady).toBe(true);
    } catch (err) {
      log('unleash client initialization failed');
      captureException(err);
    }

    log('waiting for router initialization');

    // Wait initial router navigation
    await router.isReady();

    eventBus.emit('appInitialized');

    // Check if user can stay on current route after feature flags update
    featureFlags.on('update', () => {
      reRunCurrentRouteGuards();
    });
    log('app is ready');
  } catch (err) {
    captureException(err);
    console.error(err);
    error.value = true;
    log('app initialization failed');
  } finally {
    initializing.value = false;
  }
});

watch(locale, () => {
  keyMetaStore.fetch().catch(noop);
});

useHead(
  computed(() => ({
    htmlAttrs: {
      lang: i18nStore.locale,
      dir: i18nStore.textDirection,
    },
    titleTemplate: (value) => `${value ? value + ' | ' : ''}Onebeat App`,
  })),
);

function reloadApp() {
  window.location.reload();
}

onBeforeUnmount(() => {
  featureFlags.stop();
});
</script>

<template>
  <UiConfigProvider :config="uiConfigNew">
    <ObConfigProvider :config="uiConfig">
      <UiEmptyState v-if="initializing" preset="loading" :rounded="false" style="height: 100vh" />
      <FullHeightContainer v-else-if="error" min-height="100vh">
        <UiEmptyState preset="error" :rounded="false">
          {{ t('messages.ourSystemIsUnderHeavyLoadTitle') }}
          <template #text>
            {{ t('messages.ourSystemIsUnderHeavyLoadText') }}
          </template>
          <template #content>
            <UiButton variant="secondary" @click="reloadApp()">
              {{ t('app.actions.reload') }}
            </UiButton>
          </template>
        </UiEmptyState>
      </FullHeightContainer>
      <template v-else>
        <RouterView />
        <DownloadsWidget />
        <ObToasts />
        <ObConfirmDialog />
        <GlobalAnnouncements />
      </template>
    </ObConfigProvider>
  </UiConfigProvider>
</template>

<style>
body {
  font-family: 'Poppins', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #fff;
  margin: 0;
  min-width: 1400px;
  font-optical-sizing: auto;
}
</style>
